import { useState, useCallback } from 'react';
import {
  WeatherPreferences,
  WeatherModel,
  FavoriteCity,
  WeatherPreferencesStorage,
} from '../services/weatherPreferencesStorage';
import { useWeatherPreferencesSync } from './useWeatherPreferencesSync';
import { WeatherPreferencesOperations } from '../services/weatherPreferencesOperations';
import Logger from '../../../utils/logger';

export function useWeatherPreferences() {
  const [error, setError] = useState<string | null>(null);
  const { isSyncing, debouncedSync, checkAndRetryFailedSync, fetchFromBackend } =
    useWeatherPreferencesSync();

  /**
   * Get weather preferences (localStorage first, then backend if needed)
   */
  const getWeatherPreferences = useCallback(async (): Promise<WeatherPreferences> => {
    try {
      // Check if we have local data
      if (WeatherPreferencesStorage.hasLocalData()) {
        const preferences = WeatherPreferencesStorage.getPreferences();

        // Check for failed syncs and retry if needed
        checkAndRetryFailedSync();

        return preferences;
      }

      // No local data, fetch from backend
      const preferences = await fetchFromBackend();

      // Cache the fetched data
      WeatherPreferencesStorage.savePreferences(preferences);

      return preferences;
    } catch (err) {
      Logger.error('Failed to get weather preferences', err);

      // Return empty preferences as fallback
      return {
        favoriteWeatherVariables: [],
        favoriteWeatherModels: {},
        favoriteCities: {},
        customModelColors: {},
      };
    }
  }, [checkAndRetryFailedSync, fetchFromBackend]);

  /**
   * Update preferences with optimistic updates and change detection
   */
  const updatePreferences = useCallback(
    async (
      updater: (current: WeatherPreferences) => WeatherPreferences,
      operationName: string,
    ): Promise<boolean> => {
      setError(null);

      try {
        // Get current preferences
        const currentPrefs = await getWeatherPreferences();

        // Apply the update
        const updatedPrefs = updater(currentPrefs);

        // Check if there's actually a change
        if (!WeatherPreferencesStorage.hasChanged(currentPrefs, updatedPrefs)) {
          return true; // No change needed
        }

        // Update localStorage immediately (optimistic) and mark as pending
        WeatherPreferencesStorage.savePreferences(updatedPrefs, true);

        // Sync to backend in background
        debouncedSync(updatedPrefs);

        // Reduce logging verbosity - only log the operation type, not full data
        Logger.info(`Updated ${operationName}`);
        return true;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : `Failed to ${operationName}`;
        setError(errorMessage);
        Logger.error(`Failed to ${operationName}`, err);
        return false;
      }
    },
    [getWeatherPreferences, debouncedSync],
  );

  // ============ BULK UPDATES ============

  const updateFavoriteWeatherVariables = useCallback(
    async (variables: string[]): Promise<boolean> => {
      return updatePreferences(
        (current) => ({ ...current, favoriteWeatherVariables: variables }),
        'update favorite weather variables',
      );
    },
    [updatePreferences],
  );

  const updateFavoriteWeatherModels = useCallback(
    async (models: Record<string, WeatherModel>): Promise<boolean> => {
      return updatePreferences(
        (current) => ({ ...current, favoriteWeatherModels: models }),
        'update favorite weather models',
      );
    },
    [updatePreferences],
  );

  const updateFavoriteCities = useCallback(
    async (cities: Record<string, FavoriteCity>): Promise<boolean> => {
      return updatePreferences(
        (current) => ({ ...current, favoriteCities: cities }),
        'update favorite cities',
      );
    },
    [updatePreferences],
  );

  const updateAllWeatherPreferences = useCallback(
    async (preferences: WeatherPreferences): Promise<boolean> => {
      return updatePreferences(() => preferences, 'update all weather preferences');
    },
    [updatePreferences],
  );

  const updateCustomModelColors = useCallback(
    async (colors: Record<string, string>): Promise<boolean> => {
      return updatePreferences(
        (current) => ({ ...current, customModelColors: colors }),
        'update custom model colors',
      );
    },
    [updatePreferences],
  );

  // ============ INDIVIDUAL OPERATIONS ============

  const removeFavoriteCity = useCallback(
    async (cityId: string): Promise<boolean> => {
      return updatePreferences(
        (current) => WeatherPreferencesOperations.removeFavoriteCity(current, cityId),
        `remove favorite city: ${cityId}`,
      );
    },
    [updatePreferences],
  );

  // ============ UTILITIES ============

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const generateId = useCallback(() => {
    return WeatherPreferencesOperations.generateId();
  }, []);

  const addOrUpdateCityIfNotExists = useCallback(
    async (location: {
      lat: number;
      lon: number;
      name: string;
    }): Promise<{ cityId: string; wasAdded: boolean }> => {
      try {
        // Get current preferences
        const currentPrefs = await getWeatherPreferences();

        // Check if city exists and get the result
        const result = WeatherPreferencesOperations.addOrUpdateCityIfNotExists(
          currentPrefs,
          location,
        );

        // Only update if there was a change
        if (
          result.wasAdded ||
          WeatherPreferencesStorage.hasChanged(currentPrefs, result.preferences)
        ) {
          // Update localStorage immediately (optimistic) and mark as pending
          WeatherPreferencesStorage.savePreferences(result.preferences, true);

          // Sync to backend in background
          debouncedSync(result.preferences);

          // Only log if city was actually added, not just updated
          if (result.wasAdded) {
            Logger.info(`Added new city: ${location.name}`, { cityId: result.cityId });
          }
        }

        return { cityId: result.cityId, wasAdded: result.wasAdded };
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to add/update city';
        setError(errorMessage);
        Logger.error('Failed to add/update city', err);
        return { cityId: generateId(), wasAdded: false }; // Fallback
      }
    },
    [getWeatherPreferences, debouncedSync, generateId],
  );

  const cleanupDuplicateCities = useCallback(async (): Promise<boolean> => {
    try {
      // Get current preferences
      const currentPrefs = await getWeatherPreferences();

      // Clean up duplicates
      const cleanedPrefs = WeatherPreferencesOperations.cleanupDuplicateCities(currentPrefs);

      // Only update if there was a change
      if (WeatherPreferencesStorage.hasChanged(currentPrefs, cleanedPrefs)) {
        // Update localStorage immediately and mark as pending
        WeatherPreferencesStorage.savePreferences(cleanedPrefs, true);

        // Sync to backend in background
        debouncedSync(cleanedPrefs);

        const removedCount =
          Object.keys(currentPrefs.favoriteCities).length -
          Object.keys(cleanedPrefs.favoriteCities).length;
        Logger.info(`Cleaned up ${removedCount} duplicate cities`);
        return true;
      }

      return false;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to cleanup duplicate cities';
      setError(errorMessage);
      Logger.error('Failed to cleanup duplicate cities', err);
      return false;
    }
  }, [getWeatherPreferences, debouncedSync]);

  return {
    // State
    isSyncing,
    error,

    // Getters
    getWeatherPreferences,

    // Bulk updates
    updateFavoriteWeatherVariables,
    updateFavoriteWeatherModels,
    updateFavoriteCities,
    updateAllWeatherPreferences,
    updateCustomModelColors,

    // Individual operations
    removeFavoriteCity,

    // Utilities
    clearError,
    generateId,
    addOrUpdateCityIfNotExists,
    cleanupDuplicateCities,
  };
}
